import { cn } from '@invent/wl-ui-kit-next';
import { Input } from '@invent/wl-ui-kit-next/ui';
import { FileText, Search } from 'lucide-react';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import { ChoiceSelect } from '@/design';
import type {
  IAdminSecAdvFirmDetail,
  ISecAdvCatalogue,
  ISecAdvCatalogueAttribute,
  ISecAdvCatalogueElement
} from '../../api/sec-adv-contracts';
import { formatDay } from '../../components/days';
import { CatalogueValue, asWritten, codeLabel, isObject } from '../../components/sec-adv-values';
import { recordAt } from './firm-data';
import { Figure, Panel } from './panel';
import { Dash } from './register';

const FORMS = [
  { id: 'part1', label: 'ADV Part 1', enabled: true },
  { id: 'part2', label: 'ADV Part 2', enabled: false },
  { id: 'part3', label: 'ADV Part 3', enabled: false },
  { id: 'advw', label: 'ADV-W', enabled: false }
];

/**
 * A firm's Filings (SR-423): its latest Form ADV Part 1 as the register holds it, every answer as
 * "Item 1.A. Full legal name" over its value — the catalogue's labels, never the feed's element
 * names — in the form's order, searchable across items and values. `?item=<path>.<attribute>` (or
 * an element's path) scrolls to that answer and marks it: the click-through from any Form ADV
 * value on the list or the firm page. Where the catalogue cannot be read, every answer is drawn
 * by the feed's own element and attribute names over its value as written (`rawSections`), still
 * searchable and still marked by `?item=`. Parts 2 and 3 and ADV-W, and earlier filings, wait for
 * their sources.
 */
export function FirmFilings({
  firm,
  catalogue
}: {
  firm: IAdminSecAdvFirmDetail;
  catalogue: Nullable<ISecAdvCatalogue>;
}) {
  const [params] = useSearchParams();
  const target = params.get('item');
  const [find, setFind] = React.useState('');
  const sections = React.useMemo(
    () => (catalogue ? sectionsOf(firm.record, catalogue) : rawSections(firm.record)),
    [firm.record, catalogue]
  );
  const words = find.trim().toLowerCase();
  const shown = words === '' ? sections : filterSections(sections, words);
  const [marked, setMarked] = React.useState<Nullable<string>>(null);
  const body = React.useRef<HTMLDivElement>(null);

  // The click-through: the answer named, scrolled to and marked; the search cleared first so it
  // is on the page.
  React.useEffect(() => {
    if (!target) return;
    setFind('');
    const frame = requestAnimationFrame(() => {
      const nodes = [...(body.current?.querySelectorAll<HTMLElement>('[data-source]') ?? [])];
      const node =
        nodes.find((candidate) => candidate.dataset.source === target) ??
        nodes.find((candidate) => candidate.dataset.source?.startsWith(`${target}.`));
      if (!node) return;
      node.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setMarked(node.dataset.source ?? null);
    });
    return () => cancelAnimationFrame(frame);
  }, [target, sections]);

  const filedOn = firm.filingDate ? formatDay(firm.filingDate) : null;
  const filingId =
    firm.custodians && firm.custodians.submittedAt.slice(0, 10) === firm.filingDate
      ? String(firm.custodians.filingId)
      : null;

  return (
    <Panel title="Firm filings submitted to the SEC" icon={FileText}>
      <div className="flex flex-wrap items-center gap-3">
        <div role="group" aria-label="Form" className="inline-flex rounded-md border border-border p-0.5">
          {FORMS.map((form) => (
            <button
              key={form.id}
              type="button"
              disabled={!form.enabled}
              aria-pressed={form.id === 'part1'}
              className={cn(
                'rounded px-3 py-1 text-[0.8125rem]',
                form.id === 'part1'
                  ? 'bg-[var(--color-royal-blue)] text-white'
                  : 'text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50'
              )}
            >
              {form.label}
            </button>
          ))}
        </div>
        <span className="ml-auto flex items-center gap-2">
          <span id="sec-adv-filing-choice" className="text-muted-foreground">
            Available filings
          </span>
          <ChoiceSelect
            labelledBy="sec-adv-filing-choice"
            value="latest"
            onChange={() => undefined}
            choices={[
              {
                value: 'latest',
                label: filedOn
                  ? `${filingId ? `${filingId} · ` : ''}filed on ${filedOn}`
                  : 'The latest filing'
              }
            ]}
            className="h-8 w-64"
          />
        </span>
      </div>
      <p className="text-muted-foreground">
        ADV Part 1 is the firm&apos;s main registration or reporting filing with the SEC or states.
        It covers ownership, offices, employees, clients, assets under management, business
        activities and disciplinary history.
      </p>
      <div className="grid gap-4 rounded-md border border-border p-4 sm:grid-cols-3">
        <Figure label="Filing ID">{filingId ?? <Dash />}</Figure>
        <Figure label="Form version">{firm.summary.formVersion ?? <Dash />}</Figure>
        <Figure label="Date filed">{filedOn ?? <Dash />}</Figure>
      </div>
      <label className="relative block">
        <Search
          className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          type="search"
          aria-label="Search the filing"
          placeholder="Search ADV items and values"
          value={find}
          onChange={(event) => setFind(event.target.value)}
          className="pl-8"
        />
      </label>
      <div ref={body} className="grid gap-6">
        {shown.length === 0 && (
          <p className="text-muted-foreground">
            {words === '' ? 'The filing holds no answers.' : 'No answer matches.'}
          </p>
        )}
        {shown.map((section) => (
          <section
            key={section.path}
            data-source={section.path}
            aria-label={section.title}
            className={cn(
              'grid scroll-mt-24 gap-3 rounded-md transition-shadow',
              marked === section.path && 'ring-2 ring-[var(--color-teal)] ring-offset-4'
            )}
          >
            <h3 className="text-[0.8125rem] font-medium tracking-wide text-[var(--color-royal-blue)] uppercase">
              {section.title}
            </h3>
            <div className="grid gap-3 border-l-2 border-border pl-4">
              {section.answers.map((answer) => (
                <div
                  key={answer.key}
                  data-source={answer.source}
                  className={cn(
                    'grid scroll-mt-24 gap-1 rounded-md transition-colors',
                    marked === answer.source &&
                      'bg-[color-mix(in_srgb,var(--color-teal)_10%,white)] ring-2 ring-[var(--color-teal)] ring-offset-2'
                  )}
                >
                  <span className="text-xs text-[var(--veritant-royal-70)] uppercase">{answer.label}</span>
                  <span className="rounded bg-[var(--color-off-white)] px-3 py-1.5 font-mono text-[0.8125rem] wrap-anywhere">
                    {answer.value}
                  </span>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </Panel>
  );
}

interface IAnswer {
  key: string;
  /** Its `?item=` key. */
  source: string;
  label: string;
  value: React.ReactNode;
  /** What the search reads: the label and the value in words. */
  text: string;
}

interface ISection {
  path: string;
  title: string;
  answers: IAnswer[];
}

function filterSections(sections: ISection[], words: string): ISection[] {
  return sections
    .map((section) =>
      section.title.toLowerCase().includes(words)
        ? section
        : { ...section, answers: section.answers.filter((answer) => answer.text.includes(words)) }
    )
    .filter((section) => section.answers.length > 0);
}

function sectionTitle(element: ISecAdvCatalogueElement): string {
  return element.item ? `Item ${element.item} — ${element.title}` : element.title;
}

function answerLabel(attribute: ISecAdvCatalogueAttribute): string {
  return attribute.item ? `Item ${attribute.item}. ${attribute.label}` : attribute.label;
}

/** A value in the words the search matches: as written, and a code's label. */
function valueText(attribute: ISecAdvCatalogueAttribute, value: unknown): string {
  const written = asWritten(value);
  const label = typeof value === 'string' ? codeLabel(attribute.values ?? [], value) : null;
  const yes =
    attribute.valueType !== 'yes_no' ? '' : value === 'Y' ? 'yes' : value === 'N' ? 'no' : '';
  return `${written} ${label ?? ''} ${yes}`;
}

function attributeAnswers(
  element: ISecAdvCatalogueElement,
  value: Record<string, unknown>
): IAnswer[] {
  return element.attributes
    .filter((attribute) => value[attribute.name] !== undefined)
    .map((attribute) => {
      const raw = value[attribute.name];
      const label = answerLabel(attribute);
      return {
        key: `${element.path}.${attribute.name}`,
        source: `${element.path}.${attribute.name}`,
        label,
        value:
          raw === null || raw === '' ? (
            <Dash />
          ) : (
            <CatalogueValue attribute={attribute} value={raw} />
          ),
        text: `${label} ${valueText(attribute, raw)}`.toLowerCase()
      };
    });
}

/** The record in the catalogue's order: a section per element the filing answers. */
function sectionsOf(record: unknown, catalogue: ISecAdvCatalogue): ISection[] {
  const sections: ISection[] = [];
  for (const element of catalogue.elements) {
    const value = recordAt(record, element.path);
    if (value === undefined) continue;
    const title = sectionTitle(element);
    if (Array.isArray(value)) {
      // A repeating element is an answer per entry — a notice filing's state, status and date on
      // one line.
      const label = `${element.item ? `Item ${element.item}. ` : ''}${element.title}`;
      const answers = value.map((entry, index): IAnswer => {
        const parts = isObject(entry)
          ? element.attributes.filter((attribute) => entry[attribute.name] !== undefined)
          : [];
        return {
          key: `${element.path}[${index}]`,
          source: element.path,
          label,
          value: isObject(entry)
            ? parts.map((attribute, place) => (
                <React.Fragment key={attribute.name}>
                  {place > 0 && ' · '}
                  <CatalogueValue attribute={attribute} value={entry[attribute.name]} />
                </React.Fragment>
              ))
            : asWritten(entry),
          text: `${label} ${
            isObject(entry)
              ? parts.map((attribute) => valueText(attribute, entry[attribute.name])).join(' ')
              : asWritten(entry)
          }`.toLowerCase()
        };
      });
      if (answers.length > 0) sections.push({ path: element.path, title, answers });
      continue;
    }
    if (!isObject(value) || element.attributes.length === 0) continue;
    const answers = attributeAnswers(element, value);
    if (answers.length > 0) sections.push({ path: element.path, title, answers });
  }
  return sections;
}

/**
 * The record without its catalogue, in the feed's order: a section per element holding values,
 * titled by its path, each value under its attribute's name as the feed wrote it, and a repeating
 * element an answer per entry.
 */
function rawSections(record: unknown): ISection[] {
  const sections: ISection[] = [];
  const walk = (node: Record<string, unknown>, path: string) => {
    const start = sections.length;
    const answers: IAnswer[] = [];
    for (const [name, value] of Object.entries(node)) {
      const at = path ? `${path}.${name}` : name;
      if (isObject(value)) {
        walk(value, at);
      } else if (Array.isArray(value)) {
        const entries = value.map((entry, index): IAnswer => {
          const written = isObject(entry)
            ? Object.entries(entry)
                .map(([part, one]) => `${part}: ${asWritten(one)}`)
                .join(' · ')
            : asWritten(entry);
          return rawAnswer(`${at}[${index}]`, at, name, written);
        });
        if (entries.length > 0) sections.push({ path: at, title: at, answers: entries });
      } else {
        answers.push(rawAnswer(at, at, name, value === null || value === '' ? null : asWritten(value)));
      }
    }
    // An element's own values before the elements it holds.
    if (answers.length > 0) sections.splice(start, 0, { path, title: path || 'Record', answers });
  };
  if (isObject(record)) walk(record, '');
  return sections;
}

function rawAnswer(key: string, source: string, label: string, written: Nullable<string>): IAnswer {
  return {
    key,
    source,
    label,
    value: written === null ? <Dash /> : written,
    text: `${label} ${written ?? ''}`.toLowerCase()
  };
}
